import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface EmailVerificationPromptProps {
  email: string;
  onClose?: () => void;
  variant?: "modal" | "inline";
}

export function EmailVerificationPrompt({ 
  email, 
  onClose, 
  variant = "inline" 
}: EmailVerificationPromptProps) {
  const [isResending, setIsResending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleResendEmail = async () => {
    try {
      setIsResending(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`
        }
      });

      if (error) throw error;

      setEmailSent(true);
      toast({
        title: "Verification email sent",
        description: "Please check your inbox for the verification link.",
      });
    } catch (error) {
      console.error("Error resending verification email:", error);
      toast({
        title: "Failed to resend email",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const content = (
    <>
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <Mail className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle className="text-xl">Verify Your Email Address</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-center">
        <p className="text-gray-600">
          To complete your nursing home claim, please verify your email address.
        </p>
        <p className="text-sm text-gray-500">
          We sent a verification link to: <strong>{email}</strong>
        </p>
        
        {emailSent && (
          <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Verification email sent successfully!</span>
          </div>
        )}

        <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">
            Check your spam folder if you don't see the email
          </span>
        </div>

        <div className="space-y-3 pt-4">
          <Button
            onClick={handleResendEmail}
            disabled={isResending || emailSent}
            variant="outline"
            className="w-full"
          >
            {isResending ? "Sending..." : emailSent ? "Email Sent" : "Resend Verification Email"}
          </Button>
          
          {onClose && (
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full"
            >
              Continue to Dashboard
            </Button>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-4">
          You can continue using your account, but some features may be limited until you verify your email.
        </p>
      </CardContent>
    </>
  );

  if (variant === "modal") {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          {content}
        </Card>
      </div>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      {content}
    </Card>
  );
}